import {ElasticParser, ElasticSearchProperty} from '../factory.parsers';
import {numberExp, numberGtExp, numberLtExp, NumberParser, numberRangeExp} from './number.parser';
import {defaultRaDecRadius, RaDecParserParser, radiusExp} from './ra-dec.parser';

const numberRangeAllExp = `(${numberRangeExp}|${numberGtExp}|${numberLtExp})`;
export const eclipticRangeRegExp = new RegExp(`^${numberRangeAllExp}\\s+${numberRangeAllExp}$`);
export const eclipticNoRangeRegExp = new RegExp(`^${numberExp}\\s+${numberExp}\\s*${radiusExp}$`);

export class EclipticParser implements ElasticParser {
  parseValue(eclipticString: string, elasticSearchProperty: ElasticSearchProperty): any {
    eclipticString = eclipticString?.trim();

    const {property} = elasticSearchProperty;
    const isEclipticStringAEclipticRangeRegExp = eclipticRangeRegExp.test(eclipticString);
    const isEclipticStringAEclipticNoRangeRegExp = eclipticNoRangeRegExp.test(eclipticString);

    if (isEclipticStringAEclipticRangeRegExp) {
      return this.parseEclipticRangeRegExp(eclipticString, elasticSearchProperty);
    } else if (isEclipticStringAEclipticNoRangeRegExp) {
      return this.parseEclipticNoRangeRegExp(eclipticString, property);
    } else {
      return undefined;
    }
  }

  private parseEclipticRangeRegExp(eclipticString: string, elasticSearchProperty: ElasticSearchProperty): any {
    const match = eclipticRangeRegExp.exec(eclipticString);

    const indexLongitudeRangeInMatches = 1;
    const indexLatitudeRangeInMatches = 22;

    const longitudeString = match[indexLongitudeRangeInMatches];
    const latitudeString = match[indexLatitudeRangeInMatches];

    const longitudeNumberParser = new NumberParser();
    const longitudeProperty = elasticSearchProperty.property.eclipticLongitude;
    const longitudeStringParsed = longitudeNumberParser.parseValue(longitudeString, {property: longitudeProperty});

    const latitudeNumberParser = new NumberParser();
    const latitudeProperty = elasticSearchProperty.property.eclipticLatitude;
    const latitudeStringParsed = latitudeNumberParser.parseValue(latitudeString, {property: latitudeProperty});

    return {bool: {must: [longitudeStringParsed, latitudeStringParsed]}};
  }

  private parseEclipticNoRangeRegExp(eclipticString: string, property): any {
    const match = eclipticNoRangeRegExp.exec(eclipticString);

    const indexLongitudeInMatches = 1;
    const indexLatitudeInMatches = 5;
    const indexRadiusInMatches = 9;

    const longitudeString = match[indexLongitudeInMatches];
    const latitudeString = match[indexLatitudeInMatches];
    const radiusString = match[indexRadiusInMatches];

    const longitudeNumeric = +longitudeString;
    const latitudeNumeric = +latitudeString;
    const radiusNumeric = +radiusString?.replace(',', '') || defaultRaDecRadius;

    const longitudeLatitudeToRaDegDegreesFormatter = new EclipticLongitudeLatitudeToRaDegDegreesFormatter();
    const {ra, dec} = longitudeLatitudeToRaDegDegreesFormatter.eclipticToEquatorial(longitudeNumeric, latitudeNumeric);

    const raDecRadiusProperty = {ra: property.ra, dec: property.dec, footprint: property.footprint};
    const raDecRadiusString = `${ra} ${dec}, ${radiusNumeric}`;

    const decParserParser = new RaDecParserParser();
    return decParserParser.parseValue(raDecRadiusString, {property: raDecRadiusProperty});
  }
}

export class EclipticLongitudeLatitudeToRaDegDegreesFormatter {

  eclipticToEquatorial(lambda: number, beta: number): { ra: number; dec: number } {
    // Convert degrees to radians
    const lambda_rad = lambda * Math.PI / 180;
    const beta_rad = beta * Math.PI / 180;
    const epsilon_deg = 23.439291; // J2000 obliquity
    const epsilon_rad = epsilon_deg * Math.PI / 180;
 
    const sin_lambda = Math.sin(lambda_rad);
    const cos_lambda = Math.cos(lambda_rad);
    const tan_beta = Math.tan(beta_rad);
    const sin_beta = Math.sin(beta_rad);
    const cos_beta = Math.cos(beta_rad);
    const cos_epsilon = Math.cos(epsilon_rad);
    const sin_epsilon = Math.sin(epsilon_rad);
 
    // Compute RA
    const numerator = sin_lambda * cos_epsilon - tan_beta * sin_epsilon;
    const denominator = cos_lambda;
    let alpha_rad = Math.atan2(numerator, denominator);
 
    // Ensure alpha is between 0 and 2π
    if (alpha_rad < 0) {
        alpha_rad += 2 * Math.PI;
    }
 
    // Compute Dec
    const delta_rad = Math.asin(sin_beta * cos_epsilon + cos_beta * sin_epsilon * sin_lambda);
 
    // Convert RA to degrees
    const ra_deg = alpha_rad * 180 / Math.PI;
 
    // Convert Dec to degrees
    const dec_deg = delta_rad * 180 / Math.PI;
 
    return { ra: ra_deg, dec: dec_deg };
  }
}
